import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Router, Params, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Course, CourseSubscriptionSession, CourseSubscriptionSessionProvider } from '@stuplay';
import { BackUrlService } from '../../services/back-url.service';
import { ExternalAppsService } from '../../services/external-apps.service';
import { StorageService } from '../../services/storage.service';

@Component({
    selector: 'msc-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.less']
})
export class TopBarComponent {
    @Input() course: Course | any;
    @Input() courseSubscriptionSession: CourseSubscriptionSession;
    @Input() showSidebar: boolean;
    @Input() displaySidebar: boolean;
    @Output() toggle: EventEmitter<any> = new EventEmitter();
    preview: boolean = false;
    isFromLearningPath: boolean = false;

    // Breadcrumb: sección y contenido activo derivados de la URL
    activeSection: any = null;
    activeSectionContent: any = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fromService: BackUrlService,
        private courseSubscriptionSessionProvider: CourseSubscriptionSessionProvider,
        private externalAppsService: ExternalAppsService,
        private storageService: StorageService,
    ) { }

    ngOnInit() {
        this.route.queryParams.subscribe((params: Params) => {
            this.preview = !!params['preview'];
            this.isFromLearningPath = !!params['isFromLearningPath'];
        });

        // Actualiza el breadcrumb en la carga inicial y en cada cambio de ruta
        this.updateBreadcrumbFromUrl(this.router.url);
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.updateBreadcrumbFromUrl(event.urlAfterRedirects);
            }
        });
    }

    /**
     * Lee sectionId y sectionContentId desde la URL y busca los objetos
     * correspondientes en course.sections para construir el breadcrumb:
     *   Sección activa > Contenido activo
     * Ejemplo de URL: /course/123/section/456/media/789
     */
    private updateBreadcrumbFromUrl(url: string): void {
        if (!this.course?.sections) { return; }

        // URL ejemplos:
        //   /section/780343/cheatsheet/342220                 → resourceId = cheatsheet.id
        //   /section/123/media/456/content/789                → mediaId=456, sectionContentId=789
        //   /section/123/external-link/55                     → externalLink.id
        const sectionMatch = url.match(/\/section\/(\d+)/);
        const mediaContentMatch = url.match(/\/section\/\d+\/media\/\d+\/content\/(\d+)/);
        const resourceMatch = url.match(/\/section\/\d+\/([^/?#]+)\/(\d+)/);

        const sectionId = sectionMatch ? +sectionMatch[1] : null;
        this.activeSection = sectionId
            ? this.course.sections.find((s: any) => s.id === sectionId) ?? null
            : null;

        if (!this.activeSection) {
            this.activeSectionContent = null;
            return;
        }

        const contents: any[] = this.activeSection.sectionContents ?? [];

        // Caso 1: media con sectionContentId explícito al final de la URL.
        if (mediaContentMatch) {
            const scId = +mediaContentMatch[1];
            this.activeSectionContent = contents.find((sc) => sc?.id === scId) ?? null;
            return;
        }

        // Caso 2: el segmento tras /section/<id>/ es el context y el siguiente
        // número es el id del recurso interno (no del sectionContent).
        if (resourceMatch) {
            const contextSegment = resourceMatch[1];          // 'cheatsheet' | 'external-link' | ...
            const resourceId = +resourceMatch[2];
            // 'external-link' en URL ↔ 'external_link' en el modelo
            const context = contextSegment.replace(/-/g, '_');

            this.activeSectionContent = contents.find((sc) => {
                const inner = sc?.[context] ?? sc?.[contextSegment];
                return inner?.id === resourceId || sc?.id === resourceId;
            }) ?? null;
            return;
        }

        this.activeSectionContent = null;
    }

    back(): void {
        const me = this.storageService.get('me');
        const company = this.storageService.get('company');
        parent.postMessage('closeIframePlayer', '*');
        document.location.replace(this.fromService.getBackUrl());
    }

    toggleSidebar(): void {
        this.showSidebar = !this.showSidebar;
        this.toggle.emit();
    }

    expandSidebar(): void {
        document.body.classList.remove('sidebar-collapsed');
    }

    resetCurrentSubscriptionSession(): void {
        this.courseSubscriptionSessionProvider.resetCourseSubscriptionSession(this.courseSubscriptionSession?.id).subscribe(() => {
            this.router.navigate(['course', this.course?.id], { queryParams: { preview: this.preview } });
            window.location.reload();
        });
    }
}

